import React from "react";
import { FormattedMessage } from "react-intl";
import { type PickersShortcutsItemContext, useParsedFormat, usePickerContext } from "@mui/x-date-pickers-pro";
import type { DateOrTimeViewWithMeridiem, PickerRangeValue, PickerValue } from "@mui/x-date-pickers/internals";
import { styled, type SxProps, Typography } from "@mui/material";
import { DateTime } from "luxon";

import { isDayEdgeValue } from "../../../utils/luxon";

interface DateTimeRangeTextProps {
	readonly shortcut: PickersShortcutsItemContext | undefined;
	readonly sx?: SxProps;
}

const StyledTypography = styled(Typography)({
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
});

const getPickerValueLabel = (value: PickerValue, parsedFormat: string, fieldFormat: string): string | null => {
	if (value === null) {
		return parsedFormat;
	}

	if (isDayEdgeValue(value)) {
		return value.toLocaleString(DateTime.DATE_SHORT);
	}

	return value.toFormat(fieldFormat);
};

const getDefaultPickerValueLabel = (value: PickerValue, parsedFormat: string, fieldFormat: string) => {
	if (value === null) {
		return parsedFormat;
	}

	return value.toFormat(fieldFormat);
};

const getPickerText = (dateRange: PickerRangeValue, parsedFormat: string, fieldFormat: string) => {
	if (isDayEdgeValue(dateRange[0]) === false || isDayEdgeValue(dateRange[1]) === false) {
		return dateRange
			.map((date: PickerValue) => getDefaultPickerValueLabel(date, parsedFormat, fieldFormat))
			.join(" – ");
	}

	const [startText, endText] = dateRange.map((date: PickerValue) =>
		getPickerValueLabel(date, parsedFormat, fieldFormat),
	);

	return startText === endText && startText !== parsedFormat ? startText : `${startText} - ${endText}`;
};

export const DateTimeRangeText: React.FC<DateTimeRangeTextProps> = ({ shortcut, sx }) => {
	const parsedFormat = useParsedFormat();
	const pickerContext = usePickerContext<PickerRangeValue, DateOrTimeViewWithMeridiem, string | null>();

	const formattedValue = getPickerText(pickerContext.value, parsedFormat, pickerContext.fieldFormat);

	if (shortcut && pickerContext.label) {
		return (
			<StyledTypography sx={sx}>
				<FormattedMessage
					id="date-time-range-text.shortcut-with-label"
					defaultMessage="{label}: {shortcut} {value}"
					values={{
						label: pickerContext.label,
						shortcut: shortcut.label,
						value: (
							<Typography component="span" color="info" sx={{ fontSize: "0.8rem" }}>
								<FormattedMessage
									id="date-time-range-text.value"
									defaultMessage="({value})"
									values={{ value: formattedValue }}
								/>
							</Typography>
						),
					}}
				/>
			</StyledTypography>
		);
	}

	if (shortcut) {
		return (
			<StyledTypography sx={sx}>
				<FormattedMessage
					id="date-time-range-text.shortcut"
					defaultMessage="{shortcut} {value}"
					values={{
						shortcut: shortcut.label,
						value: (
							<Typography component="span" color="info" sx={{ fontSize: "0.8rem" }}>
								<FormattedMessage
									id="date-time-range-text.value"
									defaultMessage="({value})"
									values={{ value: formattedValue }}
								/>
							</Typography>
						),
					}}
				/>
			</StyledTypography>
		);
	}

	if (pickerContext.label) {
		return (
			<StyledTypography sx={sx}>
				<FormattedMessage
					id="date-time-range-text.label-with-value"
					defaultMessage="{label}: {value}"
					values={{ label: pickerContext.label, value: formattedValue }}
				/>
			</StyledTypography>
		);
	}

	return <StyledTypography sx={sx}>{formattedValue}</StyledTypography>;
};
